﻿import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import FanTrafficOrigin from '../../components/FanTrafficOrigin'
import FanPreferencesPanel from '../../components/FanPreferencesPanel'
import FanRuntimePanel from '../../components/FanRuntimePanel'
import PaidScriptSessionPanel from '../../components/PaidScriptSessionPanel'
import SafetyLockNotice from '../../components/SafetyLockNotice'
import { resumeWithSafetyReview, safetyReviewMessage } from '../../lib/safetyReview'
import { getT } from '../../i18n.jsx'
import {
  api,
  createTestChat,
  getApiErrorMessage,
  getChatConversation,
  getChatConversations,
  getFanBotProfile,
  getChatSafetyStatus,
  getMyCapabilities,
  purgeChatConversation,
  sendManualConversationMessage,
  createVipSalesSignal,
  setChatManualMode,
  setChatVipMode,
  setChatPpvPermission,
  simulateIncomingMessage,
  simulatePurchase,
  subscribeChatEvents,
  unlockChatSafetyLock,
} from '../../api'
import { exportChatHistory } from '../../api/model_data'
import { EmptyState, LoadingBlock, MetricCard, Section, StatusPill } from '../../components/v2/V2Primitives'
import { useWorkspaceScope } from '../../contexts/WorkspaceScopeContext'
import { isAdminRole, isManagerRole, isTesterOrAdminRole } from '../../lib/realm'
import { asList, formatDate, modelTitle, platformFromConversation, providerLabel, readBool, totalOf } from './v2Utils'

const CHAT_LIMIT = 20
const MESSAGE_LIMIT = 100
const TEST_REPLY_POLL_MS = 2000
const TEST_REPLY_TIMEOUT_MS = 120000
const STYLE_TRAITS = [
  ['directness', 'Directness'],
  ['warmth_need', 'Warmth'],
  ['playfulness', 'Playfulness'],
  ['detail_preference', 'Detail'],
  ['pace', 'Pace'],
]
const PLATFORM_FILTERS = [
  ['all', 'All platforms'],
  ['onlyfansapi', 'OnlyFans'],
  ['fanvue', 'Fanvue'],
  ['telegram_account', 'Telegram'],
  ['telegram_tribute', 'Telegram Tribute'],
  ['telegram_bot_payments', 'Telegram Bot Payments'],
  ['aigirlfactory', 'AIGirlFactory'],
  ['fansly', 'Fansly'],
]

function normalizeApiPath(url) {
  if (!url || typeof url !== 'string') return null
  if (url.startsWith('/api/')) return url.slice(4)
  if (url.includes('/api/')) return `/${url.split('/api/')[1]}`
  return url
}

function AuthedImage({ url, alt = '', className }) {
  const [blobUrl, setBlobUrl] = useState(null)
  const [failed, setFailed] = useState(false)
  const blobRef = useRef(null)

  useEffect(() => {
    const path = normalizeApiPath(url)
    if (!path) return undefined

    let cancelled = false
    setFailed(false)
    api(path, { skipContentType: true })
      .then((res) => (res.ok ? res.blob() : null))
      .then((blob) => {
        if (!blob) throw new Error('empty_blob')
        const nextUrl = URL.createObjectURL(blob)
        if (cancelled) {
          URL.revokeObjectURL(nextUrl)
          return
        }
        if (blobRef.current) URL.revokeObjectURL(blobRef.current)
        blobRef.current = nextUrl
        setBlobUrl(nextUrl)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })

    return () => {
      cancelled = true
      if (blobRef.current) URL.revokeObjectURL(blobRef.current)
      blobRef.current = null
      setBlobUrl(null)
    }
  }, [url])

  if (failed || !blobUrl) return <div className={className} />
  return <img src={blobUrl} alt={alt} className={className} loading="lazy" />
}

function AuthedAudio({ url, className }) {
  const [blobUrl, setBlobUrl] = useState(null)
  const [failed, setFailed] = useState(false)
  const blobRef = useRef(null)

  useEffect(() => {
    const path = normalizeApiPath(url)
    if (!path) return undefined

    let cancelled = false
    setFailed(false)
    api(path, { skipContentType: true })
      .then((res) => (res.ok ? res.blob() : null))
      .then((blob) => {
        if (!blob) throw new Error('empty_blob')
        const nextUrl = URL.createObjectURL(blob)
        if (cancelled) {
          URL.revokeObjectURL(nextUrl)
          return
        }
        if (blobRef.current) URL.revokeObjectURL(blobRef.current)
        blobRef.current = nextUrl
        setBlobUrl(nextUrl)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })

    return () => {
      cancelled = true
      if (blobRef.current) URL.revokeObjectURL(blobRef.current)
      blobRef.current = null
      setBlobUrl(null)
    }
  }, [url])

  if (failed || !blobUrl) return <div className={className} />
  return <audio className={className} controls preload="none" src={blobUrl} />
}

function subscriptionSummary(subscription) {
  if (!subscription?.active) return ''
  const parts = []
  if (subscription.tier_name) parts.push(subscription.tier_name)
  const price = Number(subscription.price)
  if (Number.isFinite(price) && price > 0) {
    const currency = subscription.currency || 'USD'
    parts.push(new Intl.NumberFormat('en', { style: 'currency', currency }).format(price))
  }
  if (subscription.ends_at) parts.push(`through ${formatDate(subscription.ends_at)}`)
  return parts.join(' · ')
}

export default function AdminV2Inbox() {
  const {
    loading: scopeLoading,
    fanvueReviewWorkspace,
    capabilities,
    models,
    selectedModelId: modelId,
    setSelectedModelId: setModelId,
  } = useWorkspaceScope()
  const platformFilters = scopeLoading
    ? [['all', 'All conversations']]
    : fanvueReviewWorkspace
      ? [['all', 'All conversations'], ['fanvue', 'Fanvue']]
      : PLATFORM_FILTERS
  const staff = capabilities?.is_agency_member === true
  const canManage = isManagerRole() && !staff
  const canChat = canManage || (staff && capabilities.agency_permissions?.includes('chat'))
  const canTest = !staff || capabilities.agency_permissions?.includes('test')
  const canAdminister = isAdminRole()
  const [canUseDiagnostics, setCanUseDiagnostics] = useState(isTesterOrAdminRole())
  const [mode, setMode] = useState(capabilities?.agency_member_profile === 'tester_no_media' ? 'test' : 'real')
  const [platform, setPlatform] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadingThread, setLoadingThread] = useState(false)
  const [error, setError] = useState('')
  const [conversations, setConversations] = useState([])
  const [chatsTotal, setChatsTotal] = useState(0)
  const [selectedKey, setSelectedKey] = useState('')
  const [messages, setMessages] = useState([])
  const [messagesTotal, setMessagesTotal] = useState(0)
  const [fan, setFan] = useState(null)
  const [fanBotProfile, setFanBotProfile] = useState(null)
  const [profileLoading, setProfileLoading] = useState(false)
  const [profileError, setProfileError] = useState('')
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const [actionBusy, setActionBusy] = useState('')
  const [actionStatus, setActionStatus] = useState('')
  const [testFanName, setTestFanName] = useState('test_fan')
  const messagesRef = useRef(null)
  const stateRef = useRef({})
  const draftByKeyRef = useRef({})
  const selectedDraftKeyRef = useRef('')
  const pendingConversationRef = useRef(null)

  useEffect(() => {
    let alive = true
    getMyCapabilities()
      .then((result) => {
        if (alive) {
          setCanUseDiagnostics(
            !result?.is_agency_member && (isTesterOrAdminRole() || Boolean(result?.can_view_chat_diagnostics)),
          )
        }
      })
      .catch(() => {
        if (alive) setCanUseDiagnostics(isTesterOrAdminRole())
      })
    return () => { alive = false }
  }, [])
  useEffect(() => { if (capabilities?.agency_member_profile === 'tester_no_media') setMode('test') }, [capabilities?.agency_member_profile])

  const visibleConversations = useMemo(() => {
    return conversations.filter((row) => {
      if (platform !== 'all' && platformFromConversation(row) !== platform) return false
      if (statusFilter === 'blocked' && !row.is_safety_lock) return false
      if (statusFilter === 'attention' && !row.attention?.needs_attention) return false
      if (statusFilter === 'waiting' && row.last_direction !== 'in') return false
      if (statusFilter === 'opportunities' && !['watch', 'missed'].includes(row.attention?.opportunity?.status)) return false
      return true
    })
  }, [conversations, platform, statusFilter])

  const attentionConversations = useMemo(() => conversations
    .filter((row) => platform === 'all' || platformFromConversation(row) === platform)
    .filter((row) => row.attention?.needs_attention)
    .sort(compareConversationAttention), [conversations, platform])

  const selectedConversation = useMemo(() => {
    if (!visibleConversations.length) return null
    return visibleConversations.find((row) => conversationKey(row) === selectedKey) || visibleConversations[0]
  }, [selectedKey, visibleConversations])

  const selectedPair = useMemo(() => pairFromConversation(selectedConversation), [selectedConversation])
  const selectedModel = useMemo(
    () => models.find((item) => String(item.id) === String(selectedPair?.modelId)),
    [models, selectedPair?.modelId],
  )

  stateRef.current = {
    modelId,
    mode,
    page,
    selectedPair,
    messagesLength: messages.length,
    canUseDiagnostics,
  }

  useEffect(() => {
    setPage(0)
    setSelectedKey('')
  }, [modelId, mode])

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError('')
    const offset = page * CHAT_LIMIT
    getChatConversations(modelId || undefined, mode === 'test', { limit: CHAT_LIMIT, offset })
      .then((result) => {
        if (!alive) return
        applyConversationResult(result)
      })
      .catch((err) => {
        if (!alive) return
        setConversations([])
        setChatsTotal(0)
        setError(getApiErrorMessage(err, 'Failed to load conversations'))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => { alive = false }
  }, [modelId, mode, page])

  useEffect(() => {
    if (!visibleConversations.length) {
      setSelectedKey('')
      return
    }
    if (!visibleConversations.some((row) => conversationKey(row) === selectedKey)) {
      setSelectedKey(conversationKey(visibleConversations[0]))
    }
  }, [selectedKey, visibleConversations])

  useEffect(() => {
    const previousKey = selectedDraftKeyRef.current
    const currentDraft = draft
    const lastSaved = previousKey ? (draftByKeyRef.current[previousKey] || '') : ''
    if (previousKey && currentDraft !== lastSaved) {
      draftByKeyRef.current[previousKey] = currentDraft
    }
    selectedDraftKeyRef.current = selectedKey || ''
    setDraft(selectedKey ? (draftByKeyRef.current[selectedKey] || '') : '')
  }, [selectedKey])

  useEffect(() => {
    if (!selectedPair) {
      setMessages([])
      setMessagesTotal(0)
      setFan(null)
      return undefined
    }
    let alive = true
    setLoadingThread(true)
    getChatConversation(selectedPair.modelId, selectedPair.fanId, { limit: MESSAGE_LIMIT, offset: 0 })
      .then((result) => {
        if (!alive) return
        setMessages(asList(result))
        setMessagesTotal(totalOf(result, asList(result).length))
        setFan(result?.fan || null)
        setActionStatus('')
      })
      .catch((err) => {
        if (!alive) return
        setMessages([])
        setMessagesTotal(0)
        setFan(null)
        setError(getApiErrorMessage(err, 'Failed to load conversation'))
      })
      .finally(() => {
        if (alive) setLoadingThread(false)
      })
    return () => { alive = false }
  }, [selectedPair?.modelId, selectedPair?.fanId, selectedPair?.provider])

  useEffect(() => {
    if (!selectedPair || !canUseDiagnostics) {
      setFanBotProfile(null)
      setProfileError('')
      return undefined
    }
    let alive = true
    setProfileLoading(true)
    setProfileError('')
    getFanBotProfile(selectedPair.modelId, selectedPair.fanId)
      .then((result) => {
        if (alive) setFanBotProfile(result)
      })
      .catch((err) => {
        if (alive) {
          setFanBotProfile(null)
          setProfileError(getApiErrorMessage(err, 'Failed to load buyer style'))
        }
      })
      .finally(() => {
        if (alive) setProfileLoading(false)
      })
    return () => { alive = false }
  }, [selectedPair?.modelId, selectedPair?.fanId, canUseDiagnostics])

  useEffect(() => {
    if (!selectedPair || loadingThread) return undefined
    const id = window.setTimeout(() => {
      const el = messagesRef.current
      if (el) el.scrollTop = el.scrollHeight
    }, 40)
    return () => window.clearTimeout(id)
  }, [messages.length, selectedPair?.fanId, selectedPair?.modelId, loadingThread])

  useEffect(() => {
    const unsub = subscribeChatEvents(() => {
      refreshConversations(false)
      refreshThread(false)
      refreshDiagnostics(false)
    })
    return unsub
  }, [])

  useEffect(() => {
    const convTimer = window.setInterval(() => refreshConversations(false), 8000)
    const threadTimer = window.setInterval(() => refreshThread(false), 5000)
    return () => {
      window.clearInterval(convTimer)
      window.clearInterval(threadTimer)
    }
  }, [])

  async function refreshConversations(showLoading = false, overrides = null) {
    const snapshot = { ...stateRef.current, ...(overrides || {}) }
    if (showLoading) setLoading(true)
    try {
      const offset = (snapshot.page || 0) * CHAT_LIMIT
      const result = await getChatConversations(snapshot.modelId || undefined, snapshot.mode === 'test', { limit: CHAT_LIMIT, offset })
      applyConversationResult(result)
    } catch (_) {
      // Background refresh should not interrupt the operator.
    } finally {
      if (showLoading) setLoading(false)
    }
  }

  function applyConversationResult(result) {
    const rows = asList(result)
    const pending = pendingConversationRef.current
    const pendingRow = pending
      ? rows.find((row) => {
        const pair = pairFromConversation(row)
        return pair
          && String(pair.modelId) === String(pending.modelId)
          && String(pair.fanId) === String(pending.fanId)
      })
      : null

    if (pendingRow) pendingConversationRef.current = null
    setConversations(rows)
    setChatsTotal(totalOf(result, rows.length))
    setSelectedKey((current) => {
      if (pendingRow) return conversationKey(pendingRow)
      if (current && rows.some((row) => conversationKey(row) === current)) return current
      return rows[0] ? conversationKey(rows[0]) : ''
    })
  }

  async function refreshThread(showLoading = false) {
    const snapshot = stateRef.current
    const pair = snapshot.selectedPair
    if (!pair) return
    if (showLoading) setLoadingThread(true)
    try {
      const limit = Math.max(MESSAGE_LIMIT, Number(snapshot.messagesLength || 0) + 25)
      const result = await getChatConversation(pair.modelId, pair.fanId, { limit, offset: 0 })
      setMessages(asList(result))
      setMessagesTotal(totalOf(result, asList(result).length))
      setFan(result?.fan || null)
    } catch (_) {
      // Keep current thread visible if a background refresh fails.
    } finally {
      if (showLoading) setLoadingThread(false)
    }
  }

  async function refreshDiagnostics(showLoading = false) {
    const snapshot = stateRef.current
    const pair = snapshot.selectedPair
    if (!pair || !snapshot.canUseDiagnostics) return
    if (showLoading) setProfileLoading(true)
    try {
      const result = await getFanBotProfile(pair.modelId, pair.fanId)
      setFanBotProfile(result)
      setProfileError('')
    } catch (err) {
      if (showLoading) setProfileError(getApiErrorMessage(err, 'Failed to load buyer style'))
    } finally {
      if (showLoading) setProfileLoading(false)
    }
  }

  async function loadEarlierMessages() {
    if (!selectedPair || messages.length >= messagesTotal) return
    setActionBusy('earlier')
    try {
      const result = await getChatConversation(selectedPair.modelId, selectedPair.fanId, { limit: MESSAGE_LIMIT, offset: messages.length })
      const older = asList(result)
      if (older.length) setMessages((prev) => [...older, ...prev])
      setMessagesTotal(totalOf(result, messagesTotal))
    } catch (err) {
      setActionStatus(getApiErrorMessage(err, 'Failed to load earlier messages'))
    } finally {
      setActionBusy('')
    }
  }

  async function sendTestMessage() {
    if (!selectedPair || !draft.trim()) return
    const pair = { ...selectedPair }
    setSending(true)
    setActionStatus('Sending test message...')
    try {
      const simulated = await simulateIncomingMessage(pair.modelId, pair.fanId, draft.trim())
      const incomingMessageId = Number(simulated?.message_id || 0)
      updateDraft('')
      setActionStatus('Test message queued — waiting for AI reply...')
      await refreshThread(false)
      await refreshConversations(false)

      const startedAt = Date.now()
      let replyReceived = false
      while (Date.now() - startedAt < TEST_REPLY_TIMEOUT_MS) {
        await wait(TEST_REPLY_POLL_MS)
        const currentPair = stateRef.current.selectedPair
        if (!sameConversation(currentPair, pair)) return

        const result = await getChatConversation(pair.modelId, pair.fanId, {
          limit: Math.max(MESSAGE_LIMIT, Number(stateRef.current.messagesLength || 0) + 25),
          offset: 0,
        })
        const rows = asList(result)
        setMessages(rows)
        setMessagesTotal(totalOf(result, rows.length))
        setFan(result?.fan || null)

        replyReceived = rows.some((message) => (
          message.direction === 'out'
          && (!incomingMessageId || Number(message.id || 0) > incomingMessageId)
        ))
        if (replyReceived) {
          const seconds = Math.max(1, Math.round((Date.now() - startedAt) / 1000))
          setActionStatus(`AI replied in ${seconds}s`)
          await refreshConversations(false)
          break
        }
      }
      if (!replyReceived) {
        setActionStatus('No AI reply after 120 seconds. Check diagnostics or bot logs.')
      }
    } catch (err) {
      setActionStatus(getApiErrorMessage(err, 'Failed to send test message'))
    } finally {
      setSending(false)
    }
  }

  async function sendManualMessage() {
    if (!selectedPair || !draft.trim()) return
    setSending(true)
    setActionStatus('')
    try {
      await sendManualConversationMessage(selectedPair.modelId, selectedPair.fanId, draft.trim())
      updateDraft('')
      await refreshThread(true)
      await refreshConversations(false)
    } catch (err) {
      setActionStatus(getApiErrorMessage(err, 'Failed to send manual message'))
    } finally {
      setSending(false)
    }
  }

  function updateDraft(value) {
    setDraft(value)
    if (selectedKey) {
      draftByKeyRef.current[selectedKey] = value
    }
  }

  async function createTestConversation() {
    const targetModelId = modelId || models[0]?.id
    if (!targetModelId) return
    const targetModel = models.find((item) => String(item.id) === String(targetModelId))
    const defaultConfigId = targetModel?.default_config_id ?? undefined
    setActionBusy('create-test')
    setActionStatus('')
    try {
      const created = await createTestChat(targetModelId, {
        username: testFanName.trim() || 'test_fan',
        name: testFanName.trim() || 'test_fan',
        configId: defaultConfigId,
      })
      pendingConversationRef.current = created?.fan_id == null
        ? null
        : {
          modelId: created.model_id ?? targetModelId,
          fanId: created.fan_id,
        }
      setMode('test')
      setModelId(String(targetModelId))
      setPage(0)
      setTestFanName('test_fan')
      await refreshConversations(true, { modelId: targetModelId, mode: 'test', page: 0 })
      setActionStatus('Test chat created')
    } catch (err) {
      setActionStatus(getApiErrorMessage(err, 'Failed to create test chat'))
    } finally {
      setActionBusy('')
    }
  }

  async function toggleManualMode() {
    if (!selectedPair) {
      setActionStatus('Select a conversation before changing its mode.')
      return
    }
    const next = !isManualMode
    const pair = { ...selectedPair }
    setActionBusy('manual')
    setActionStatus('')
    try {
      const result = next
        ? await setChatManualMode(pair.modelId, pair.fanId, true)
        : await resumeWithSafetyReview(
          (review) => setChatManualMode(pair.modelId, pair.fanId, false, review),
          (message) => sameConversation(stateRef.current.selectedPair, pair) && window.confirm(message),
          `model #${pair.modelId}, fan #${pair.fanId}`,
          getT(),
        )
      if (!result) { setActionStatus('Unlock cancelled. Automatic replies remain blocked.'); return }
      if (!sameConversation(stateRef.current.selectedPair, pair)) { await refreshConversations(false); return }
      const resolvedManualMode = typeof result?.is_manual_mode === 'boolean'
        ? result.is_manual_mode
        : next
      setFan((prev) => (
        prev ? { ...prev, is_manual_mode: resolvedManualMode } : prev
      ))
      setFanBotProfile((prev) => (
        prev ? { ...prev, is_manual_mode: resolvedManualMode } : prev
      ))
      setConversations((prev) => prev.map((row) => {
        const pair = pairFromConversation(row)
        return sameConversation(pair, selectedPair)
          ? { ...row, is_manual_mode: resolvedManualMode }
          : row
      }))
      await Promise.all([
        refreshConversations(false),
        refreshThread(false),
        refreshDiagnostics(false),
      ])
      setActionStatus(
        result?.safety_lock?.locked
          ? 'Automatic replies remain safety-locked. Review the current lock before resuming.'
          : resolvedManualMode
          ? 'Manual mode enabled'
          : result?.resume_status === 'queued'
            ? 'Auto mode enabled. Full history catch-up and analysis started.'
            : 'Manual mode disabled',
      )
    } catch (err) {
      setActionStatus(getApiErrorMessage(err, 'Failed to change manual mode'))
    } finally {
      setActionBusy('')
    }
  }

  async function toggleVipMode() {
    if (!selectedPair || !fan) return
    const pair = { ...selectedPair }
    const next = !fan.is_vip_mode
    setActionBusy('vip')
    setActionStatus('')
    try {
      const result = await setChatVipMode(pair.modelId, pair.fanId, next)
      if (!sameConversation(stateRef.current.selectedPair, pair)) { await refreshConversations(false); return }
      setFan((prev) => (
        prev ? { ...prev, is_vip_mode: result?.is_vip_mode ?? next,
          ppv_sales_enabled: result.ppv_sales_enabled,
          ppv_permission_revision: result.ppv_permission_revision } : prev
      ))
      await refreshConversations(false)
      setActionStatus(
        result?.safety_lock?.locked
          ? 'VIP setting saved. Automatic replies remain safety-locked; review and unlock first.'
          : next
          ? 'VIP enabled. PPV permission is unchanged.'
          : 'VIP disabled. PPV permission is unchanged.',
      )
    } catch (err) {
      setActionStatus(getApiErrorMessage(err, 'Failed to change VIP mode'))
    } finally {
      setActionBusy('')
    }
  }

  async function togglePpvPermission() {
    if (!selectedPair || !fan || actionBusy) return
    const pair = { ...selectedPair }
    const next = !isPpvEnabled
    setActionBusy('ppv')
    setActionStatus('')
    try {
      const result = await setChatPpvPermission(pair.modelId, pair.fanId, next,
        fan.ppv_permission_revision ?? selectedConversation?.ppv_permission_revision)
      if (!sameConversation(stateRef.current.selectedPair, pair)) { await refreshConversations(false); return }
      const snapshot = { ppv_sales_enabled: result.ppv_sales_enabled,
        ppv_permission_revision: result.ppv_permission_revision }
      setFan((prev) => prev ? { ...prev, ...snapshot } : prev)
      setFanBotProfile((prev) => prev ? { ...prev, ...snapshot } : prev)
      await refreshConversations(false)
      setActionStatus(next
        ? 'PPV permitted when Core gates pass. VIP, manual mode and safety locks are unchanged.'
        : 'Automatic PPV disabled, including pending offers. Other permissions are unchanged.')
    } catch (err) {
      if (sameConversation(stateRef.current.selectedPair, pair)) {
        setActionStatus(getApiErrorMessage(err, 'Failed to change PPV permission'))
        await refreshThread(false)
      }
    } finally {
      setActionBusy('')
    }
  }

  async function signalVipSale() {
    if (!selectedPair || !fan?.is_vip_mode) return
    const instruction = window.prompt(
      'Human sales instruction (what to sell, how to position it, or what to check):',
    )
    if (!instruction?.trim()) return
    setActionBusy('vip-sale')
    setActionStatus('')
    try {
      await createVipSalesSignal(selectedPair.modelId, selectedPair.fanId, {
        opportunity_type: 'sale',
        instruction: instruction.trim(),
      })
      setActionStatus('VIP sales signal saved and sent to the operator channel.')
    } catch (err) {
      setActionStatus(getApiErrorMessage(err, 'Failed to create VIP sales signal'))
    } finally {
      setActionBusy('')
    }
  }

  async function unlockSelectedChat() {
    if (!selectedPair) return
    const pair = { ...selectedPair }
    setActionBusy('unlock')
    setActionStatus('')
    try {
      const { safety_lock: lock } = await getChatSafetyStatus(pair.modelId, pair.fanId)
      if (!sameConversation(stateRef.current.selectedPair, pair)) return
      if (!lock?.locked) { await refreshConversations(false); setActionStatus('No active safety lock'); return }
      if (lock?.locked && !window.confirm(safetyReviewMessage(lock, { target: `model #${pair.modelId}, fan #${pair.fanId}`, t: getT() }))) return
      const result = await unlockChatSafetyLock(pair.modelId, pair.fanId, lock?.confirmation_token)
      if (!sameConversation(stateRef.current.selectedPair, pair)) { await refreshConversations(false); return }
      setFan((prev) => (
        prev && typeof result?.is_manual_mode === 'boolean'
          ? { ...prev, is_manual_mode: result.is_manual_mode }
          : prev
      ))
      await refreshConversations(true)
      setActionStatus('Safety lock unlocked')
    } catch (err) {
      setActionStatus(getApiErrorMessage(err, 'Failed to unlock chat'))
    } finally {
      setActionBusy('')
    }
  }

  async function deleteSelectedChat() {
    if (!selectedPair) return
    if (!window.confirm('Delete this conversation and related bot state? This cannot be undone.')) return
    setActionBusy('delete')
    setActionStatus('')
    try {
      await purgeChatConversation(selectedPair.modelId, selectedPair.fanId)
      setSelectedKey('')
      setMessages([])
      setFan(null)
      await refreshConversations(true)
      setActionStatus('Conversation deleted')
    } catch (err) {
      setActionStatus(getApiErrorMessage(err, 'Failed to delete conversation'))
    } finally {
      setActionBusy('')
    }
  }

  async function copyHistory() {
    if (!selectedPair) return
    setActionBusy('copy')
    setActionStatus('')
    try {
      const result = await exportChatHistory(selectedPair.modelId, selectedPair.fanId)
      if (result?.text) await navigator.clipboard.writeText(result.text)
      setActionStatus('Chat history copied')
    } catch (err) {
      setActionStatus(getApiErrorMessage(err, 'Failed to copy chat history'))
    } finally {
      setActionBusy('')
    }
  }

  async function buyOffer(message) {
    if (!selectedPair || !message?.id) return
    setActionBusy(`buy-${message.id}`)
    setActionStatus('')
    try {
      await simulatePurchase(selectedPair.modelId, selectedPair.fanId, {
        messageId: message.id,
        amount: message.purchase_offer?.price ?? 0,
        purchaseType: 'ppv_unlock',
      })
      await refreshThread(true)
      setActionStatus('Test purchase simulated')
    } catch (err) {
      setActionStatus(getApiErrorMessage(err, 'Failed to simulate purchase'))
    } finally {
      setActionBusy('')
    }
  }

  const totalPages = Math.max(1, Math.ceil(chatsTotal / CHAT_LIMIT))
  const liveCount = conversations.filter((row) => !row.is_test).length
  const blockedCount = conversations.filter((row) => row.is_safety_lock).length
  const isTestConversation = Boolean(fan?.is_test || selectedConversation?.is_test)
  const canWriteTest = Boolean(canTest && fan?.is_test && selectedPair)
  const isManualMode = Boolean(
    fan?.is_manual_mode
    ?? fanBotProfile?.is_manual_mode
    ?? selectedConversation?.is_manual_mode,
  )
  const isVipMode = Boolean(
    fan?.is_vip_mode
    ?? selectedConversation?.is_vip_mode,
  )
  const isPpvEnabled = Boolean(fan?.ppv_sales_enabled
    ?? selectedConversation?.ppv_sales_enabled ?? !isVipMode)
  const canWriteManual = Boolean(
    canChat && selectedPair && !isTestConversation
    && (isManualMode || isVipMode)
  )
  const interactionStyle = fanBotProfile?.interaction_style && typeof fanBotProfile.interaction_style === 'object'
    ? fanBotProfile.interaction_style
    : (fanBotProfile?.parameters?.interaction_style_runtime || {})
  const interactionTraits = interactionStyle?.traits && typeof interactionStyle.traits === 'object'
    ? interactionStyle.traits
    : {}
  const buyerStyle = fanBotProfile?.buyer_style_hint
    || fanBotProfile?.user_profile?.buyer_style_hint
    || 'unknown'
  const conversationStyle = fanBotProfile?.conversation_style_hint
    || fanBotProfile?.user_profile?.conversation_style_hint
    || 'unknown'
  const liveBotPaused = readBool(selectedModel?.bot_paused)
  const selectedAttention = selectedConversation?.attention || {}
  const selectedOpportunity = selectedAttention?.opportunity || {}
  const conversationReview = fanBotProfile?.parameters?.conversation_health_review || {}
  const inspectorNextAction = conversationReview?.next_action
    || selectedAttention?.next_action
    || selectedOpportunity?.recommended_action
    || ''
  const inspectorStrategy = conversationReview?.recommended_strategy
    || selectedAttention?.recommended_strategy
    || '—'
  const actionStatusIsError = /failed|no ai reply|timed out|unable/i.test(actionStatus)

  if (scopeLoading) return <LoadingBlock label="Loading inbox workspace..." />

  return (
    <div className="v2-stack v2-inbox-page">
      <div className="v2-metrics-grid">
        <MetricCard label="Visible dialogs" value={visibleConversations.length} detail={`${chatsTotal} total in current mode`} />
        <MetricCard label="Needs attention" value={attentionConversations.length} detail="Current page and provider scope" tone={attentionConversations.length ? 'warn' : 'good'} />
        <MetricCard label="Live dialogs" value={liveCount} detail="Current page" tone="good" />
        <MetricCard label="Safety locks" value={blockedCount} detail="Need operator review" tone={blockedCount ? 'warn' : 'neutral'} />
      </div>

      <Section eyebrow="Inbox" title="Full conversation workspace" description="A complete operator chat view: real and test dialogs, full message history, safe actions and platform-aware context.">
        {error ? <div className="v2-error">{error}</div> : null}
        {actionStatus ? <div aria-live="polite" className={actionStatusIsError ? 'v2-error' : 'v2-info'}>{actionStatus}</div> : null}

        <div className="v2-chat-toolbar">
          <label className="v2-filter-field">
            <span>Mode</span>
            <div className="v2-segmented-control">
              {capabilities?.agency_member_profile !== 'tester_no_media' ? <button type="button" className={mode === 'real' ? 'is-active' : ''} onClick={() => setMode('real')}>Real</button> : null}
              {canTest ? <button type="button" className={mode === 'test' ? 'is-active' : ''} onClick={() => setMode('test')}>Test</button> : null}
            </div>
          </label>
          <label className="v2-filter-field">
            <span>Model</span>
            <select value={modelId} onChange={(event) => setModelId(event.target.value)}>
              <option value="">All models</option>
              {models.map((model) => <option key={model.id} value={model.id}>{modelTitle(model)}</option>)}
            </select>
          </label>
          <label className="v2-filter-field">
            <span>Provider</span>
            <select value={platform} onChange={(event) => setPlatform(event.target.value)}>
              {platformFilters.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label className="v2-filter-field">
            <span>Status</span>
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="all">All dialogs</option>
              <option value="attention">Needs attention</option>
              <option value="waiting">Waiting for reply</option>
              <option value="opportunities">Opportunities</option>
              <option value="blocked">Safety locks only</option>
            </select>
          </label>
        </div>

        {mode === 'test' && canTest ? (
          <div className="v2-test-composer">
            <div>
              <strong>Create test chat</strong>
              <p>Creates a test fan for the selected model without touching live provider dialogs.</p>
            </div>
            <input value={testFanName} onChange={(event) => setTestFanName(event.target.value)} placeholder="test_fan" />
            <button type="button" className="v2-btn v2-btn-secondary" disabled={actionBusy === 'create-test' || !models.length} onClick={createTestConversation}>
              {actionBusy === 'create-test' ? 'Creating...' : 'Create test chat'}
            </button>
          </div>
        ) : null}

        <section className="v2-attention-queue" aria-label="Needs attention and opportunities">
          <div className="v2-attention-queue-head">
            <div>
              <p className="v2-kicker">Priority queue</p>
              <strong>Needs attention</strong>
              <span>{attentionConversations.length ? `${attentionConversations.length} items on this page` : 'No urgent items on this page'}</span>
            </div>
            <button type="button" className="v2-btn v2-btn-ghost" onClick={() => setStatusFilter(statusFilter === 'attention' ? 'all' : 'attention')}>
              {statusFilter === 'attention' ? 'Show all dialogs' : 'Filter queue'}
            </button>
          </div>
          {attentionConversations.length ? (
            <div className="v2-attention-cards">
              {attentionConversations.slice(0, 6).map((row) => {
                const primary = row.attention?.primary || {}
                const tone = attentionTone(primary.priority)
                return (
                  <button
                    type="button"
                    key={`attention-${conversationKey(row)}`}
                    className={`v2-attention-card is-${tone}`}
                    onClick={() => setSelectedKey(conversationKey(row))}
                  >
                    <span>{primary.label || 'Review conversation'}</span>
                    <strong>{conversationTitle(row)}</strong>
                    <small>{primary.detail || row.preview || 'Open conversation'}</small>
                    <em>{formatRelativeTime(row.last_at)}</em>
                  </button>
                )
              })}
            </div>
          ) : <p className="v2-attention-empty">Nothing currently requires operator action.</p>}
        </section>

        <div className="v2-inbox-layout v2-inbox-layout-full">
          <aside className="v2-source-panel v2-chat-sidebar">
            <div className="v2-conversation-list v2-conversation-list-scroll">
              {loading ? <LoadingBlock label="Loading dialogs..." /> : null}
              {!loading && visibleConversations.length ? visibleConversations.map((row) => (
                <button
                  type="button"
                  key={conversationKey(row)}
                  className={conversationKey(row) === conversationKey(selectedConversation) ? 'is-active' : ''}
                  onClick={() => setSelectedKey(conversationKey(row))}
                >
                  <span>{providerLabel(platformFromConversation(row))}</span>
                  <strong>{conversationTitle(row)}</strong>
                  <em>{row.model_email || row.model_name || `Model #${row.model_id || ''}`}</em>
                  <small>{row.preview || row.last_message_text || 'No messages yet'}</small>
                  <div className="v2-chat-list-meta">
                    {row.attention?.primary ? <StatusPill tone={attentionTone(row.attention.primary.priority)}>{row.attention.primary.label}</StatusPill> : null}
                    {row.is_test ? <StatusPill tone="warn">test</StatusPill> : <StatusPill tone="good">live</StatusPill>}
                    {row.subscription?.active ? <StatusPill tone="good">subscriber</StatusPill> : null}
                    {row.is_manual_mode ? <StatusPill tone="warn">manual</StatusPill> : null}
                    {row.is_vip_mode ? <StatusPill tone="warn">VIP</StatusPill> : null}
                    {row.is_safety_lock ? <StatusPill tone="bad">locked</StatusPill> : null}
                    <time dateTime={row.last_at || undefined}>{formatRelativeTime(row.last_at)}</time>
                  </div>
                </button>
              )) : null}
              {!loading && !visibleConversations.length ? <EmptyState title="No dialogs match these filters" description="Change provider, status or model filter." /> : null}
            </div>
            <div className="v2-pagination">
              <button type="button" className="v2-btn v2-btn-ghost" disabled={page <= 0 || loading} onClick={() => setPage((prev) => Math.max(0, prev - 1))}>Previous</button>
              <span>{page + 1} / {totalPages}</span>
              <button type="button" className="v2-btn v2-btn-ghost" disabled={page + 1 >= totalPages || loading} onClick={() => setPage((prev) => prev + 1)}>Next</button>
            </div>
          </aside>

          <main className="v2-conversation-panel v2-chat-thread">
            {!selectedConversation ? (
              <EmptyState title="Select a conversation" description="The full message history will appear here." />
            ) : loadingThread ? (
              <LoadingBlock label="Loading full chat..." />
            ) : (
              <>
                <header className="v2-chat-header">
                  <div className="v2-chat-identity">
                    <div className="v2-avatar">{conversationTitle(selectedConversation).slice(0, 1).toUpperCase()}</div>
                    <div>
                      <p className="v2-kicker">{providerLabel(platformFromConversation(selectedConversation))}</p>
                      <h3>{fan?.username || fan?.name || conversationTitle(selectedConversation)}</h3>
                      <p>{selectedModel ? modelTitle(selectedModel) : selectedConversation.model_email || `Model #${selectedPair?.modelId}`}</p>
                      {fan?.subscription?.active ? (
                        <p className="v2-chat-subscription">
                          Active subscriber{subscriptionSummary(fan.subscription) ? ` · ${subscriptionSummary(fan.subscription)}` : ''}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className="v2-chat-actions">
                    <StatusPill tone={isTestConversation ? 'warn' : 'good'}>{isTestConversation ? 'test' : 'live'}</StatusPill>
                    {fan?.subscription?.active ? <StatusPill tone="good">subscriber</StatusPill> : null}
                    {isTestConversation ? (
                      <StatusPill tone="good">test engine active</StatusPill>
                    ) : (
                      <>
                        {liveBotPaused ? <StatusPill tone="bad">bot paused</StatusPill> : <StatusPill tone="good">bot active</StatusPill>}
                        {isManualMode ? <StatusPill tone="warn">manual</StatusPill> : null}
                        {isVipMode ? <StatusPill tone="warn">VIP · operator-guided</StatusPill> : null}
                      </>
                    )}
                    {isTestConversation && liveBotPaused ? <StatusPill>live automation paused</StatusPill> : null}
                    {selectedConversation.is_safety_lock ? <StatusPill tone="bad">safety lock</StatusPill> : null}
                  </div>
                </header>

                <div className="v2-chat-body">
                  <div ref={messagesRef} className="v2-message-list">
                    <SafetyLockNotice lock={selectedConversation.safety_lock} locked={selectedConversation.is_safety_lock} />
                    {messagesTotal > messages.length ? (
                      <button type="button" className="v2-load-earlier" disabled={actionBusy === 'earlier'} onClick={loadEarlierMessages}>
                        {actionBusy === 'earlier' ? 'Loading...' : `Load earlier (${messagesTotal - messages.length})`}
                      </button>
                    ) : null}
                    {messages.length ? messages.map((message) => (
                      <MessageBubble
                        key={message.id || `${message.created_at}-${message.direction}-${message.text}`}
                        message={message}
                        fan={fan}
                        onBuy={buyOffer}
                        busy={actionBusy === `buy-${message.id}`}
                      />
                    )) : <EmptyState title="No messages" description="This conversation has no imported messages yet." />}
                  </div>

                  {canWriteTest || canWriteManual ? (
                    <div className="v2-thread-composer">
                      <textarea
                        rows={2}
                        value={draft}
                        onChange={(event) => updateDraft(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' && !event.shiftKey) {
                            event.preventDefault()
                            if (canWriteTest) sendTestMessage()
                            else sendManualMessage()
                          }
                        }}
                        placeholder={canWriteTest ? 'Send incoming test message...' : 'Write as the model...'}
                      />
                      <button
                        type="button"
                        className="v2-btn v2-btn-primary"
                        disabled={sending || !draft.trim()}
                        onClick={canWriteTest ? sendTestMessage : sendManualMessage}
                      >
                        {sending ? (canWriteTest ? 'Waiting for AI...' : 'Sending...') : (canWriteTest ? 'Send test message' : 'Send manual reply')}
                      </button>
                    </div>
                  ) : canChat ? (
                    <div className="v2-thread-readonly-note">Live provider replies are sent by the bot. Enable manual mode, or VIP mode for AI chat with human-controlled sales.</div>
                  ) : null}
                </div>
              </>
            )}
          </main>

          <aside className="v2-conversation-panel v2-chat-inspector" aria-label="Conversation context and controls">
            {!selectedConversation ? (
              <EmptyState title="Conversation context" description="Select a dialog to see state, strategy and controls." />
            ) : (
              <>
                <header className="v2-inspector-head">
                  <div>
                    <p className="v2-kicker">Conversation brain</p>
                    <h3>Context</h3>
                  </div>
                  {selectedAttention?.primary ? <StatusPill tone={attentionTone(selectedAttention.primary.priority)}>{selectedAttention.primary.label}</StatusPill> : null}
                </header>

                <section className="v2-inspector-section">
                  <span className="v2-inspector-label">Current state</span>
                  <strong>{fanBotProfile?.conversation_state || fanBotProfile?.current_state || selectedConversation.conversation_state || selectedConversation.current_state || 'RAPPORT'}</strong>
                  <div className="v2-inspector-pills">
                    <StatusPill>{fanBotProfile?.relationship_stage || selectedConversation.relationship_stage || 'relationship unknown'}</StatusPill>
                    <StatusPill>{fanBotProfile?.monetization_stage || selectedConversation.monetization_stage || 'commercial unknown'}</StatusPill>
                  </div>
                </section>

                {(canAdminister || capabilities?.traffic_intelligence_enabled) && selectedPair && (
                  <FanTrafficOrigin modelId={selectedPair.modelId} fanId={selectedPair.fanId} />
                )}

                <section className="v2-inspector-section">
                  <span className="v2-inspector-label">Strategy</span>
                  <strong>{inspectorStrategy}</strong>
                  <p>{inspectorNextAction || 'No explicit next action is recorded yet.'}</p>
                </section>

                {['watch', 'missed'].includes(selectedOpportunity?.status) ? (
                  <section className={`v2-inspector-section v2-inspector-opportunity is-${selectedOpportunity.status}`}>
                    <span className="v2-inspector-label">Commercial opportunity</span>
                    <strong>{humanizeToken(selectedOpportunity.type || 'opportunity')}</strong>
                    <p>{selectedOpportunity.reason || selectedOpportunity.recommended_action || 'Review the conversation progression.'}</p>
                    <div className="v2-inspector-meta">
                      <span>{selectedOpportunity.confidence || 0}% confidence</span>
                      <span>{humanizeToken(selectedOpportunity.current_timing || 'later')}</span>
                    </div>
                  </section>
                ) : null}

                {canUseDiagnostics ? (
                  <section className="v2-inspector-section" aria-label="Buyer style diagnostics">
                    <span className="v2-inspector-label">Adaptive style</span>
                    {profileError ? <p className="v2-buyer-style-error">{profileError}</p> : null}
                    <dl className="v2-inspector-definition-list">
                      <div><dt>Buyer</dt><dd>{profileLoading ? 'Loading...' : buyerStyle}</dd></div>
                      <div><dt>Conversation</dt><dd>{profileLoading ? 'Loading...' : conversationStyle}</dd></div>
                      <div><dt>Adaptive</dt><dd>{interactionStyle?.status || (interactionStyle?.active ? 'active' : 'not enough data')}</dd></div>
                      <div><dt>Confidence</dt><dd>{Number.isFinite(Number(interactionStyle?.confidence)) ? `${Number(interactionStyle.confidence)}%` : '—'}</dd></div>
                    </dl>
                    <div className="v2-inspector-traits">
                      {STYLE_TRAITS.map(([key, label]) => (
                        <span key={key}><em>{label}</em><strong>{interactionTraits[key] || '—'}</strong></span>
                      ))}
                    </div>
                  </section>
                ) : null}

                {canUseDiagnostics && selectedPair && <FanPreferencesPanel key={`${selectedPair.modelId}:${selectedPair.fanId}`} modelId={selectedPair.modelId} fanId={selectedPair.fanId} />}
                {canUseDiagnostics && selectedPair && <FanRuntimePanel key={`runtime:${selectedPair.modelId}:${selectedPair.fanId}`} modelId={selectedPair.modelId} fanId={selectedPair.fanId} />}
                {canManage && selectedPair && !isTestConversation && <PaidScriptSessionPanel key={`prepaid:${selectedPair.modelId}:${selectedPair.fanId}`} modelId={selectedPair.modelId} fanId={selectedPair.fanId} manualMode={isManualMode} />}

                <section className="v2-inspector-section v2-inspector-controls">
                  <span className="v2-inspector-label">Controls</span>
                  {canManage && selectedPair?.modelId ? <Link to={`/v2/models/${selectedPair.modelId}`} className="v2-btn v2-btn-ghost">Open model</Link> : null}
                  {canChat && selectedPair && !isTestConversation ? <button type="button" className={`v2-btn ${isManualMode ? 'v2-btn-secondary' : 'v2-btn-ghost'}`} aria-pressed={isManualMode} title={isManualMode ? 'Disable manual mode' : 'Enable manual mode'} disabled={Boolean(actionBusy)} onClick={toggleManualMode}>{isManualMode ? 'Manual mode: ON' : 'Manual mode: OFF'}</button> : null}
                  {canManage && selectedPair && !isTestConversation ? <button type="button" className="v2-btn v2-btn-ghost" disabled={Boolean(actionBusy)} onClick={toggleVipMode}>{isVipMode ? 'Disable VIP' : 'Enable VIP'}</button> : null}
                  {canManage && selectedPair ? <label className="v2-btn v2-btn-ghost" title="Independent of VIP. Allows catalog PPV only when Core, safety and timing gates pass; does not send an offer now.">
                    <input type="checkbox" checked={isPpvEnabled} disabled={Boolean(actionBusy) || !fan} onChange={togglePpvPermission} /> Allow PPV
                  </label> : null}
                  {canManage && isVipMode ? <button type="button" className="v2-btn v2-btn-secondary" disabled={actionBusy === 'vip-sale'} onClick={signalVipSale}>Signal sale</button> : null}
                  {canManage && selectedConversation.is_safety_lock ? <button type="button" className="v2-btn v2-btn-secondary" disabled={Boolean(actionBusy)} onClick={unlockSelectedChat}>Review / unlock safety lock</button> : null}
                  {canUseDiagnostics ? <button type="button" className="v2-btn v2-btn-ghost" disabled={actionBusy === 'copy'} onClick={copyHistory}>Copy history</button> : null}
                  {canAdminister ? <button type="button" className="v2-btn v2-btn-danger" disabled={actionBusy === 'delete'} onClick={deleteSelectedChat}>Delete chat</button> : null}
                </section>
              </>
            )}
          </aside>
        </div>
      </Section>
    </div>
  )
}

function MessageBubble({ message, fan, onBuy, busy }) {
  const isOut = message.direction === 'out'
  const text = stripHtml(message.text || '')
  const attachments = Array.isArray(message.attachments) ? message.attachments : []
  const incomingMedia = Array.isArray(message.incoming_media) ? message.incoming_media : []
  const purchaseOffer = message.purchase_offer
  const offerPrice = Number(purchaseOffer?.price_int ?? purchaseOffer?.price ?? 0)
  const paidMediaCount = Number(purchaseOffer?.paid_media_count || 0)
  const mediaRedacted = Boolean(message.media_redacted || purchaseOffer?.media_redacted || attachments.some((item) => item?.redacted))
  const showBuy = Boolean(fan?.is_test && isOut && purchaseOffer && !purchaseOffer.is_purchased && !purchaseOffer.is_free_group && (offerPrice > 0 || (purchaseOffer.paid_media_ids || []).length > 0 || paidMediaCount > 0 || purchaseOffer.has_paid_media))

  return (
    <article className={`v2-message-bubble-wrap ${isOut ? 'is-out' : 'is-in'}`}>
      <div className="v2-message-bubble">
        <div className="v2-message-head">
          <strong>{isOut ? 'Model' : 'Fan'}</strong>
          <span>{formatTime(message.created_at)}</span>
        </div>
        {attachments.length ? <AttachmentStrip attachments={attachments} outgoing /> : null}
        {isOut && mediaRedacted && attachments.length ? <p className="v2-message-media-redacted">Creator media hidden in tester mode. Delivery metadata remains available for QA.</p> : null}
        {incomingMedia.length ? <AttachmentStrip attachments={incomingMedia} /> : null}
        <p>{text || (attachments.length || incomingMedia.length ? 'Media message' : 'No text')}</p>
        {purchaseOffer ? (
          <div className="v2-offer-card">
            <span>PPV offer</span>
            <strong>{offerPrice > 0 ? `$${offerPrice}` : 'Free'}</strong>
            <em>{purchaseOffer.is_purchased ? 'Purchased' : 'Not purchased'}</em>
            {showBuy ? <button type="button" className="v2-btn v2-btn-secondary" disabled={busy} onClick={() => onBuy(message)}>{busy ? 'Buying...' : 'Simulate purchase'}</button> : null}
          </div>
        ) : null}
      </div>
    </article>
  )
}

function AttachmentStrip({ attachments, outgoing = false }) {
  return (
    <div className="v2-message-media">
      {attachments.map((item, index) => <MediaAttachment key={item.id || item.media_id || item.url || index} item={item} outgoing={outgoing} />)}
    </div>
  )
}

function MediaAttachment({ item, outgoing }) {
  const type = item.media_type || item.type || 'media'
  const url = item.preview_url || item.thumb_url || item.full_url || item.url || ''
  const isApiUrl = typeof url === 'string' && url.startsWith('/api/')
  const isImage = ['photo', 'gif', 'image'].includes(String(type).toLowerCase())
  const isAudio = String(type).toLowerCase() === 'audio'

  if (outgoing && item.redacted) {
    return (
      <span className="v2-message-attachment v2-message-attachment-redacted">
        Model media sent
        <em>{type} · hidden preview</em>
      </span>
    )
  }

  if (!outgoing && url && isImage) {
    return isApiUrl
      ? <AuthedImage url={url} alt="" className="v2-message-image" />
      : <img src={url} alt="" className="v2-message-image" loading="lazy" />
  }

  if (!outgoing && url && isAudio) {
    return isApiUrl
      ? <AuthedAudio url={url} className="v2-message-audio" />
      : <audio className="v2-message-audio" controls preload="none" src={url} />
  }

  return (
    <span className="v2-message-attachment">
      {outgoing ? 'Model media' : 'Fan media'}
      <em>{type}{item.media_id ? ` #${item.media_id}` : ''}</em>
    </span>
  )
}

function conversationKey(row) {
  if (!row) return ''
  return `${platformFromConversation(row)}:${row.model_id || row.modelId || 'm'}:${row.fan_id || row.fanId || row.id || 'f'}`
}

function pairFromConversation(row) {
  if (!row) return null
  const modelId = row.model_id ?? row.modelId
  const fanId = row.fan_id ?? row.fanId ?? row.id
  if (modelId == null || fanId == null) return null
  return { modelId, fanId, provider: platformFromConversation(row) }
}

function conversationTitle(row) {
  return row?.fan_username || row?.username || row?.fan_name || row?.name || (row?.fan_id ? `Fan #${row.fan_id}` : 'Unknown fan')
}

function stripHtml(html) {
  if (!html || typeof html !== 'string') return ''
  if (typeof document === 'undefined') return html.replace(/<[^>]+>/g, '')
  const tmp = document.createElement('div')
  tmp.innerHTML = html
  return tmp.textContent || tmp.innerText || ''
}

function formatTime(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleString()
}

function sameConversation(left, right) {
  return Boolean(
    left && right
    && String(left.modelId) === String(right.modelId)
    && String(left.fanId) === String(right.fanId)
    && String(left.provider || '') === String(right.provider || ''),
  )
}

function attentionTone(priority) {
  if (priority === 'critical') return 'bad'
  if (priority === 'high' || priority === 'medium') return 'warn'
  return 'neutral'
}

function compareConversationAttention(left, right) {
  const rank = { critical: 0, high: 1, medium: 2, low: 3 }
  const leftRank = rank[left?.attention?.primary?.priority] ?? 9
  const rightRank = rank[right?.attention?.primary?.priority] ?? 9
  if (leftRank !== rightRank) return leftRank - rightRank
  return new Date(right?.last_at || 0).getTime() - new Date(left?.last_at || 0).getTime()
}

function formatRelativeTime(value) {
  if (!value) return 'No activity'
  const timestamp = new Date(value).getTime()
  if (!Number.isFinite(timestamp)) return 'Unknown time'
  const minutes = Math.max(0, Math.floor((Date.now() - timestamp) / 60000))
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

function humanizeToken(value) {
  const text = String(value || '').trim().replaceAll('_', ' ')
  return text ? `${text.slice(0, 1).toUpperCase()}${text.slice(1)}` : '—'
}

function wait(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms))
}
